<script setup lang="ts">
import type { CaseListItem } from "@maimu/contracts";
import { computed, onMounted, ref } from "vue";

import { api } from "@/lib/api.js";

import CaseDetailView from "./CaseDetailView.vue";

/**
 * Toimikud (D21).
 *
 * A toimik is only visible to its members, so this list is short by design. An admin
 * additionally sees the ones they are not in, greyed, with a way to join that the
 * toimik's own timeline records.
 */
const emit = defineEmits<{ (event: "open-chat", caseId: string): void }>();

const cases = ref<CaseListItem[]>([]);
const openId = ref<string | null>(null);
const loading = ref(true);
const busy = ref(false);
const error = ref<string | null>(null);
const creating = ref(false);
const draft = ref({ title: "", code: "", members: "" });

const mine = computed(() => cases.value.filter((item) => item.member));
const others = computed(() => cases.value.filter((item) => !item.member));

const STATUS = { open: "avatud", resolved: "lahendatud", archived: "arhiveeritud" } as const;

function fail(cause: unknown, fallback: string): void {
  error.value = cause instanceof Error ? cause.message : fallback;
}

async function load(): Promise<void> {
  loading.value = true;
  try {
    cases.value = await api.cases();
  } catch (cause) {
    fail(cause, "Toimikuid ei õnnestunud laadida.");
  } finally {
    loading.value = false;
  }
}

async function create(): Promise<void> {
  busy.value = true;
  error.value = null;
  try {
    const created = await api.createCase({
      title: draft.value.title,
      ...(draft.value.code.trim() ? { code: draft.value.code.trim() } : {}),
      ...(draft.value.members.trim() ? { members: draft.value.members.split(/[,\s]+/).filter(Boolean) } : {}),
    });
    draft.value = { title: "", code: "", members: "" };
    creating.value = false;
    await load();
    openId.value = created.id;
  } catch (cause) {
    fail(cause, "Toimiku loomine ebaõnnestus.");
  } finally {
    busy.value = false;
  }
}

async function join(item: CaseListItem): Promise<void> {
  if (!confirm(`Liituda toimikuga "${item.title}"? See jääb toimiku ajajoonele kirja.`)) return;

  busy.value = true;
  try {
    await api.joinCase(item.id);
    await load();
    openId.value = item.id;
  } catch (cause) {
    fail(cause, "Liitumine ebaõnnestus.");
  } finally {
    busy.value = false;
  }
}

const when = (iso: string): string =>
  new Date(iso).toLocaleString("et-EE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

onMounted(load);
</script>

<template>
  <CaseDetailView
    v-if="openId"
    :case-id="openId"
    @back="((openId = null), load())"
    @open-chat="emit('open-chat', $event)"
  />

  <section v-else class="cases">
    <header class="head">
      <div>
        <h2>Toimikud</h2>
        <p class="hint">
          Üks toimik = üks probleem või töö. Kokkuvõte, ajajoon, vestlused ja failid ühes kohas, jagatud liikmete vahel.
          Toimikus alustatud vestlus algab alati toimiku kokkuvõttest.
        </p>
      </div>
      <button class="btn" type="button" @click="creating = !creating">
        {{ creating ? "Loobu" : "Uus toimik" }}
      </button>
    </header>

    <form v-if="creating" class="new" @submit.prevent="create()">
      <label class="field">
        <span>Pealkiri</span>
        <input v-model.trim="draft.title" type="text" placeholder="Directo lisaväli ei jõua liidesesse" />
      </label>
      <label class="field narrow">
        <span>Kood (valikuline)</span>
        <input v-model.trim="draft.code" type="text" spellcheck="false" placeholder="JAHK_DIRECTO_FIELD" />
      </label>
      <label class="field">
        <span>Liikmed (valikuline)</span>
        <input v-model.trim="draft.members" type="text" spellcheck="false" placeholder="triin, taimar" />
      </label>
      <button class="btn" type="submit" :disabled="busy || !draft.title">Loo</button>
    </form>

    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="loading" class="hint">Laen…</p>

    <p v-else-if="!cases.length" class="empty">
      Ühtegi toimikut veel ei ole. Loo esimene, kui mõni teema hakkab mitme vestluse peale laiali valguma.
    </p>

    <ul v-else class="list">
      <li v-for="item in mine" :key="item.id">
        <button type="button" @click="openId = item.id">
          <span class="title">
            {{ item.title }}
            <code v-if="item.code">{{ item.code }}</code>
            <span class="badge" :class="item.status">{{ STATUS[item.status] }}</span>
          </span>
          <span class="meta">
            <span>{{ item.members.join(", ") }}</span>
            <span>{{ item.conversations }} vestlust</span>
            <span>{{ item.notes }} märkust</span>
            <span v-if="item.files">{{ item.files }} faili</span>
            <span>{{ when(item.updatedAt) }}</span>
          </span>
        </button>
      </li>
    </ul>

    <template v-if="others.length">
      <h3 class="others">Toimikud, mille liige sa ei ole</h3>
      <p class="hint">
        Administraatorina näed, et need on olemas, aga mitte nende sisu. Liitumine jääb toimiku ajajoonele kirja.
      </p>
      <ul class="list muted">
        <li v-for="item in others" :key="item.id">
          <div class="row">
            <span class="title">
              {{ item.title }}
              <code v-if="item.code">{{ item.code }}</code>
            </span>
            <span class="meta">{{ item.members.join(", ") }}</span>
            <button class="btn-ghost" type="button" :disabled="busy" @click="join(item)">Liitu</button>
          </div>
        </li>
      </ul>
    </template>
  </section>
</template>

<style scoped>
.cases {
  max-width: 900px;
  margin: 0 auto;
  padding: 1.4rem 1rem 3rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

h2 {
  margin: 0 0 0.3rem;
  font-size: 1.05rem;
}

h3.others {
  margin: 1rem 0 0.2rem;
  font-size: 0.9rem;
}

.hint {
  color: var(--muted);
  font-size: 0.82rem;
  margin: 0;
  max-width: 640px;
}

.new {
  display: flex;
  gap: 0.6rem;
  align-items: flex-end;
  flex-wrap: wrap;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 0.8rem 0.9rem;
  background: var(--paper);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1 1 200px;
}

.field.narrow {
  flex: 0 0 200px;
}

.field > span {
  font-size: 0.78rem;
  font-weight: 600;
}

input {
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
  font: inherit;
  font-size: 0.86rem;
  background: #fff;
  color: var(--ink);
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.list.muted {
  opacity: 0.7;
}

.list button,
.list .row {
  width: 100%;
  text-align: left;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--paper);
  padding: 0.7rem 0.9rem;
  font: inherit;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.list .row {
  flex-direction: row;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.list button {
  cursor: pointer;
}

.list button:hover {
  border-color: var(--green);
}

.title {
  font-weight: 600;
  color: var(--ink);
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  flex: 1;
}

code {
  background: var(--soft);
  padding: 0.05rem 0.35rem;
  border-radius: 4px;
  font-size: 0.76rem;
  font-weight: 500;
}

.badge {
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 0.02rem 0.45rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--muted);
}

.badge.open {
  color: var(--green);
  border-color: var(--green);
}

.badge.archived {
  opacity: 0.6;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  font-size: 0.75rem;
  color: var(--muted);
}

.empty,
.error {
  border: 1px dashed var(--line);
  border-radius: 10px;
  padding: 1rem;
  color: var(--muted);
  font-size: 0.86rem;
  margin: 0;
}

.error {
  border-style: solid;
  border-color: var(--warn);
  color: var(--warn);
}
</style>
